/*
 *
 * dashBoardMfaPage action/reducer
 *
 */

import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { mfaFlow } from "utils/enum";
import { createActionDispatchers } from "utils/reduxHelpers";
import { AppAuthenticatorType } from "./types";
import { getTnCForCidpUser, setUserAcceptedNewTnC } from "scenes/redux";

export type State = {
  loading: boolean;
  showError: boolean;
  authenticatorAppList: Array<AppAuthenticatorType>;
  isAccountActivated: boolean;
  isDashBoardMfaRedirectEnabled: boolean;
  currentStep: string;
  appAuthenticatorSetupDone: boolean;
  appType: string;
  isPasswordValidationDone: boolean;
};

export const initialState: State = {
  loading: false,
  showError: false,
  authenticatorAppList: [],
  isAccountActivated: false,
  isDashBoardMfaRedirectEnabled: false,
  currentStep: "initial",
  appAuthenticatorSetupDone: false,
  appType: "",
  isPasswordValidationDone: false,
};

const slice = createSlice({
  name: "dashBoardMfa",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    onLoading(state: State) {
      state.loading = true;
    },
    onRemoveLoading(state: State, action: PayloadAction<string>) {
      state.loading = true;
      state.appType = action.payload;
      state.appAuthenticatorSetupDone = false;
    },
    onLoad(state: State, action: PayloadAction<any>) {
      state.loading = false;
      state.isDashBoardMfaRedirectEnabled = false;
      state.authenticatorAppList = action.payload;
    },
    isRedirectEnable(state: State, action: PayloadAction<boolean>) {
      state.isDashBoardMfaRedirectEnabled = action.payload;
    },
    resetState(state: State, action: PayloadAction<any>) {
      if (action.payload === true) {
        state.currentStep = mfaFlow.MFA;
        state.appAuthenticatorSetupDone = true;
      } else {
        state.currentStep = action.payload || mfaFlow.MFA;
      }
      state.showError = false;
    },
    onDelete(state: State, action: PayloadAction<boolean>) {
      state.loading = false;
      state.currentStep = mfaFlow.MFA;
      state.appAuthenticatorSetupDone = action.payload;
    },
    onPasswordValidateSuccess(state: State, action: PayloadAction<boolean>) {
      state.loading = false;

      if (action.payload === true) {
        state.currentStep = mfaFlow.MFA;
        state.isPasswordValidationDone = true;
        state.showError = false;
      } else {
        state.showError = true;
      }
    },
    onResetError(state: State) {
      state.showError = false;
    },
  },
});

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("dashBoardMfa/pureAction");
export const onPasswordValidate = createAction<any>(`dashBoardMfa/onPassword`);
/**
 * actions from slice to be exported for saga
 */
export const {
  onLoading,
  onLoad,
  isRedirectEnable,
  resetState,
  onDelete,
  onRemoveLoading,
  onPasswordValidateSuccess,
  onResetError,
} = slice.actions;

/**
 * all actions for layout
 */

export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  // pure actions
  [
    "onLoading",
    "onLoad",
    "isRedirectEnable",
    "resetState",
    "onRemoveLoading",
    "onDelete",
    "onResetError",
  ],
  {
    pureAction,
    onPasswordValidate,
    getTnCForCidpUser,
    setUserAcceptedNewTnC,

    goBackTo: () => push("/dashboard/securitysettings"),
    goToOktaVerifyScreen: () =>
      push("/okta-verify", {
        navigationFrom: "/dashboard/Mfa",
        currentLayout: mfaFlow.MFA,
      }),
    goToAppAuthenticatorScreen: () =>
      push("/dashboard/env-auth-verify", {
        navigationFrom: "/dashboard/Mfa",
        currentLayout: mfaFlow.MFA,
      }),
    goToAppPhoneScreen: () =>
      push("/phone", {
        navigationFrom: "/dashboard/Mfa",
        currentLayout: mfaFlow.MFA,
      }),
    goToEmailOtpScreen: () =>
      push("/dashboard/email-otp", {
        navigationFrom: "/dashboard/Mfa",
        currentLayout: mfaFlow.MFA,
      }),
    goToEnvAuthenticatorScreen: () =>
      push("/env-auth-verify", {
        navigationFrom: "/dashboard/Mfa",
        currentLayout: mfaFlow.MFA,
      }),
    goToDashboard: () => push("/dashboard/home"),
  }
);

export default slice.reducer;
